// Implements: F-UIX-010
/**
 * The sentence a person reads for a failure.
 *
 * The backend's own message is shown ONLY for the codes annex §11.1 enumerates:
 * their messages are written to be read. Anything else (a proxy page, a code
 * invented later, the `UNKNOWN` of a body that did not match the envelope)
 * gets the caller's fallback, so a future backend message can never reach
 * somebody untranslated or carrying something it should not.
 *
 * A caller that KNOWS the route it called writes its refusals for people (the
 * turn of a conversation answers a busy mockup with `CONFLICT` and a full
 * process with `UNAVAILABLE`) may vouch for more codes. The list widens only
 * that call: the default stays the annex's five.
 *
 * A usage refusal (`USAGE_LIMIT_REACHED`, phase 7) is written here from its `reason` and
 * `renewsAt` for every caller (`usageRefusal`), so no screen can show it as a generic failure.
 */

import type { ApiFailure } from "../../../types/errors";

import { USAGE_LIMIT_REACHED, usageRefusalText } from "./usageRefusal";

const NAMED_CODES: ReadonlySet<string> = new Set([
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "VALIDATION",
  "RATE_LIMITED",
]);

/** The refusals of starting a turn that the server writes for the person: busy, at capacity. */
export const TURN_REFUSAL_CODES = ["CONFLICT", "UNAVAILABLE"] as const;

/**
 * What starting a turn may also be refused with: the composer sends a body that can carry an
 * image, and a body over the limit is a 413 whose sentence the server writes for the person.
 */
export const START_TURN_REFUSAL_CODES = [...TURN_REFUSAL_CODES, "PAYLOAD_TOO_LARGE"] as const;

export function userMessage(
  error: ApiFailure,
  fallback: string,
  alsoVouchedFor: readonly string[] = [],
): string {
  // A usage refusal is never a generic failure: the client writes it from its reason (UIX.2).
  if (error.code === USAGE_LIMIT_REACHED) return usageRefusalText(error.reason, error.renewsAt);
  return NAMED_CODES.has(error.code) || alsoVouchedFor.includes(error.code)
    ? error.message
    : fallback;
}
